import { useRef, useState, useEffect } from 'react';
import stylesMobile from './MangaChaptersMobile.module.css';
import stylesDesktop from './MangaChaptersDesktop.module.css';
import MangaChaptersListPart from './components/MangaChaptersListPart/MangaChaptersListPart';
import { useMediaQuery } from 'react-responsive';

const MangaChaptersMobile = ({ mangaid, chapters, loading, error, mangaTitle }) => {
  const [dir, setDir] = useState(true);
  const [isExpanded, setIsExpanded] = useState(false);
  const sourceRef = useRef(null);
  const targetRef = useRef(null);

  useEffect(() => {
    if (!sourceRef.current || !targetRef.current) return;

    const observer = new ResizeObserver(entries => {
      for (let entry of entries) {
        targetRef.current.style.width = `${entry.contentRect.width}px`;
      }
    });

    observer.observe(sourceRef.current);
    return () => observer.disconnect();
  }, []);

  const changeDir = () => {
    setDir(prev => !prev);
  };

  if (loading) return 
    <div className={stylesMobile.statusMessage}>
      <div className={stylesMobile.statusContent}>
        <img 
          src="/logo/logo_mini_256_256_2.png" 
          alt="Загрузка..." 
          className={stylesMobile.loading_icon} 
        />
        <p style={{transform: "translateY(-30px)", marginBottom: "0px"}}>Загрузка...</p>
      </div>
    </div>;

  if (error) return <div className={stylesMobile.statusMessage}>
    <div className={stylesMobile.statusContent} style={{flex: "1"}}>
      <p style={{width: "80%", margin: "20px 100px"}}>{error}</p>
    </div>
  </div>;

  const sortedChapters = [...(chapters || [])].sort((a, b) => {
    return dir 
      ? parseFloat(b.volume_number) - parseFloat(a.volume_number) || parseFloat(b.chapter_number) - parseFloat(a.chapter_number)
      : parseFloat(a.volume_number) - parseFloat(b.volume_number) || parseFloat(a.chapter_number) - parseFloat(b.chapter_number);
  });

  const maxInitialChapters = 4;
  const visibleChapters = isExpanded 
    ? sortedChapters 
    : sortedChapters.slice(0, maxInitialChapters);


  // Структурированные данные для списка глав
  const chaptersStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    'name': `Главы манги ${mangaTitle}`,
    'numberOfItems': sortedChapters.length,
    'itemListElement': sortedChapters.slice(0, 10).map((chapter, index) => ({
      '@type': 'ListItem',
      'position': index + 1,
      'item': {
        '@type': 'Chapter',
        'name': `Глава ${chapter.chapter_number}${chapter.title ? ` - ${chapter.title}` : ''}`,
        'url': `${typeof window !== 'undefined' ? window.location.origin : ''}/reader/${mangaid}/${chapter.chapter_number}`,
        'datePublished': chapter.updated_at
      }
    }))
  };

  return (
    <div className={stylesMobile.over_cont}>
      {/* Структурированные данные для SEO */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(chaptersStructuredData) }}
      />

      <div className={stylesMobile.chapterCont}>
        {visibleChapters.length > 0 ? (
          <>
            <p id="dir" className={stylesMobile.two_sides} onClick={changeDir}>
              {dir ? <img alt="" src="/icons/arrow_up_small.svg" style={{transform: "rotate(180deg)"}}/> : <img alt="" src="/icons/arrow_up_small.svg"/>}
              {dir ? "С конца" : "С начала"}
            </p>
            {visibleChapters.map((chapter, index) => (
              <MangaChaptersListPart 
                key={index}
                tome={chapter.tome}
                volume={chapter.volume_number}
                chapter={chapter.chapter_number} 
                time={chapter.updated_at} 
                chapter_title={chapter.title}
                project_title={chapters[0].project_title}
                likes={chapter.likes}
                chapterid={chapter.id}
                ref={sourceRef}
              />
            ))}
            {sortedChapters.length > maxInitialChapters && (
              <button 
                onClick={() => setIsExpanded(!isExpanded)}
                className={stylesMobile.showMoreButton}
                ref={targetRef}
              >
                <img className={`${stylesMobile.see_icon} ${isExpanded ? stylesMobile.see_icon_rotated : ''}`} src="/icons/arrow_down.svg" alt=""/>
                <p className={stylesMobile.chapter_text}>
                    <span style={{alignItems: "left !important", justifyContent: "left", transform: "translateX(10px)"}} className={stylesMobile.chapter_tome}>{isExpanded ? 'Свернуть' : `Все главы`}</span>
                </p>
              </button>
            )}
          </>
        ) : (
          <div className={stylesMobile.statusMessage}>
            <div className={stylesMobile.statusContent} style={{flex: "1"}}>
              <p style={{width: "80%", margin: "20px 100px"}}>Тут пусто</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const MangaChaptersDesktop = ({ mangaid, chapters, loading, error, mangaTitle }) => {
  const [dir, setDir] = useState(true);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isFirstChapterHovered, setIsFirstChapterHovered] = useState(false);
  const sourceRef = useRef(null);
  const targetRef = useRef(null);

  useEffect(() => {
    if (!sourceRef.current || !targetRef.current) return;

    const observer = new ResizeObserver(entries => {
      for (let entry of entries) {
        targetRef.current.style.width = `${entry.contentRect.width}px`;
      }
    });

    observer.observe(sourceRef.current);
    return () => observer.disconnect();
  }, []);

  const changeDir = () => {
    setDir(prev => !prev);
  };

  if (loading) return <div className={stylesDesktop.statusMessage}>
    <div className={stylesDesktop.statusContent}>
      <img 
        src="/logo/logo_mini_256_256_2.png" 
        alt="Загрузка..." 
        className={styles.loading_icon} 
      />
      <p style={{transform: "translateY(-30px)", marginBottom: "0px"}}>Загрузка...</p>
    </div>
  </div>;

  if (error) return <div className={stylesDesktop.statusMessage}>
    <div className={stylesDesktop.statusContent} style={{flex: "1"}}>
      <p style={{width: "80%", margin: "20px 100px"}}>{error}</p>
    </div>
  </div>;

  const sortedChapters = [...(chapters || [])].sort((a, b) => {
    return dir 
      ? parseFloat(b.volume_number) - parseFloat(a.volume_number) || parseFloat(b.chapter_number) - parseFloat(a.chapter_number)
      : parseFloat(a.volume_number) - parseFloat(b.volume_number) || parseFloat(a.chapter_number) - parseFloat(b.chapter_number);
  });

  const maxInitialChapters = 4;
  const visibleChapters = isExpanded 
    ? sortedChapters 
    : sortedChapters.slice(0, maxInitialChapters);

  // Структурированные данные для списка глав
  const chaptersStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    'name': `Главы манги ${mangaTitle}`,
    'numberOfItems': sortedChapters.length,
    'itemListElement': sortedChapters.slice(0, 10).map((chapter, index) => ({
      '@type': 'ListItem',
      'position': index + 1,
      'item': {
        '@type': 'Chapter',
        'name': `Глава ${chapter.chapter_number}${chapter.title ? ` - ${chapter.title}` : ''}`,
        'url': `${typeof window !== 'undefined' ? window.location.origin : ''}/reader/${mangaid}/${chapter.chapter_number}`,
        'datePublished': chapter.updated_at
      }
    }))
  };

  return (
    <div className={stylesDesktop.over_cont}>

      {/* Структурированные данные для SEO */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(chaptersStructuredData) }}
      />

      <div className={stylesDesktop.chapterCont}>
        {visibleChapters.length > 0 ? (
          <>
            <p id="dir" className={`${stylesDesktop.two_sides} ${isFirstChapterHovered ? stylesDesktop.lifted : ''}`} onClick={changeDir}>
              {dir ? <img alt="" src="/icons/arrow_up_small.svg" style={{transform: "rotate(180deg)"}}/> : <img alt="" src="/icons/arrow_up_small.svg"/>}
              {dir ? "С конца" : "С начала"}
            </p>
            {visibleChapters.map((chapter, index) => (
              <MangaChaptersListPart 
                key={index}
                tome={chapter.tome}
                volume={chapter.volume_number}
                chapter={chapter.chapter_number}
                chapter_title={chapter.title}
                time={chapter.updated_at}
                project_title={chapters[0].project_title}
                likes={chapter.likes}
                chapterid={chapter.id}
                ref={sourceRef}
                onMouseEnter={index === 0 ? () => setIsFirstChapterHovered(true) : undefined}
                onMouseLeave={index === 0 ? () => setIsFirstChapterHovered(false) : undefined}
              />
            ))}
            {sortedChapters.length > maxInitialChapters && (
              <button 
                onClick={() => setIsExpanded(!isExpanded)}
                className={stylesDesktop.showMoreButton}
                ref={targetRef}
              >
                <img className={`${stylesDesktop.see_icon} ${isExpanded ? stylesDesktop.see_icon_rotated : ''}`} src="/icons/arrow_down.svg" alt=""/>
                <p className={stylesDesktop.chapter_text}>
                  <span style={{alignItems: "left !important", justifyContent: "left"}} className={stylesDesktop.chapter_tome}>{isExpanded ? 'Свернуть' : `Все главы`}</span>
                </p>
              </button>
            )}
          </>
        ) : (
          <div className={stylesDesktop.statusMessage}>
            <div className={stylesDesktop.statusContent} style={{flex: "1"}}>
              <p style={{width: "80%", margin: "20px 100px"}}>{error}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const MangaChapters = ({ mangaid, chapters, loading, error, fetchChapters, manga }) => {
  const isDesktop = useMediaQuery({ minWidth: 768 });

  useEffect(() => {
    if (!chapters && !loading && !error) {
      fetchChapters();
    }
  }, [chapters, loading, error, fetchChapters]);

  return isDesktop ? (
    <MangaChaptersDesktop 
      mangaid={mangaid}
      chapters={chapters}
      loading={loading}
      error={error}
      mangaTitle={manga?.title}
    />
  ) : (
    <MangaChaptersMobile 
      mangaid={mangaid}
      chapters={chapters}
      loading={loading}
      error={error}
      mangaTitle={manga?.title}
    />
  );
};

export default MangaChapters;